// =============================================================================
// PAGES/ADMINPARAMETROSNORMATIVOS.TSX - Motor de Enquadramento CAIXA v1
// =============================================================================
// Etapa 5 do COMANDO_CODE_MOTOR_ENQUADRAMENTO_V1.md (09/07/2026). Grid de
// parâmetros vigentes + histórico por chave + upload da Tabela de Municípios
// FGTS. Sem edição inline de valor vigente - só nova vigência (mesmo padrão
// de AdminUsuarios.tsx).
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { parametrosNormativosApi, municipiosFgtsApi, type ParametroNormativo } from '../services/api';

export default function AdminParametrosNormativos() {
  const [parametros, setParametros] = useState<ParametroNormativo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [chaveHistorico, setChaveHistorico] = useState<string | null>(null);
  const [historico, setHistorico] = useState<ParametroNormativo[]>([]);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function carregar() {
    setCarregando(true);
    setErro('');
    try {
      const dados = await parametrosNormativosApi.listar();
      setParametros(dados);
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar parâmetros.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  async function abrirHistorico(chave: string) {
    setChaveHistorico(chave);
    try {
      const dados = await parametrosNormativosApi.historico(chave);
      setHistorico(dados);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Falha ao carregar histórico.');
    }
  }

  async function importarArquivo(arquivo: File) {
    setImportando(true);
    try {
      const resumo = await municipiosFgtsApi.importar(arquivo);
      toast.success(
        `Importação concluída: ${resumo.inseridos} inseridos, ${resumo.novaVigencia} nova-vigência, ` +
        `${resumo.inalterados} inalterados (${resumo.dormentes} fora do perímetro SP, ` +
        `${resumo.divergenciasCrossCheck} com override de RM).`,
        { duration: 10000 },
      );
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro
        ?? (e instanceof Error ? e.message : 'Falha ao importar planilha.');
      toast.error(msg, { duration: 10000 });
    } finally {
      setImportando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold text-slate-800 mb-1">Motor de Enquadramento - Parâmetros Normativos</h1>
      <p className="text-sm text-slate-500 mb-5">
        Parâmetros vigentes usados pelo motor de enquadramento CAIXA (Faixa 1/2/3, Classe Média,
        Pró-Cotista, SBPE). Valor vigente nunca é editado - toda mudança abre uma nova vigência
        (o registro anterior é preservado no histórico).
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700">Tabela de Municípios FGTS</p>
          <p className="text-xs text-slate-500">Upload da planilha oficial CAIXA (.xlsx ou .csv) para atualizar os tetos por município.</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv"
          disabled={importando}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) importarArquivo(f); }}
          className="hidden"
          id="upload-municipios"
        />
        <label
          htmlFor="upload-municipios"
          className={`text-sm px-3 py-2 rounded-lg cursor-pointer ${importando ? 'bg-slate-200 text-slate-400' : 'bg-otm-orange text-white hover:bg-otm-orange-dark'}`}
        >
          {importando ? 'Importando…' : 'Importar planilha'}
        </label>
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{erro}</div>
      )}

      {carregando ? (
        <div className="text-center text-slate-400 py-12">Carregando parâmetros…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Chave</th>
                <th className="text-left px-4 py-3">Valor</th>
                <th className="text-left px-4 py-3">Fonte</th>
                <th className="text-left px-4 py-3 w-36">Vigência desde</th>
                <th className="text-left px-4 py-3 w-24">Histórico</th>
              </tr>
            </thead>
            <tbody>
              {parametros.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.chave}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-sm truncate" title={JSON.stringify(p.valor)}>
                    {JSON.stringify(p.valor)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{p.fonte}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.vigenciaInicio).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => abrirHistorico(p.chave)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {chaveHistorico && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setChaveHistorico(null)}>
          <div className="bg-white rounded-xl p-5 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-800 font-mono text-sm">{chaveHistorico}</h2>
              <button onClick={() => setChaveHistorico(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-2">Valor</th>
                  <th className="text-left py-2">Vigência início</th>
                  <th className="text-left py-2">Vigência fim</th>
                  <th className="text-left py-2">Fonte</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="py-2 max-w-xs truncate" title={JSON.stringify(h.valor)}>{JSON.stringify(h.valor)}</td>
                    <td className="py-2">{new Date(h.vigenciaInicio).toLocaleDateString('pt-BR')}</td>
                    <td className="py-2">{h.vigenciaFim ? new Date(h.vigenciaFim).toLocaleDateString('pt-BR') : <span className="text-green-600 font-medium">vigente</span>}</td>
                    <td className="py-2">{h.fonte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
